<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import XlButton from './XlButton.vue';
import { isBusy, retry } from '../../helpers/pageLoad';

/**
 * "Still loading — your connection looks slow", with Retry (prototype
 * `.slowsay`). Past five seconds an animation stops reassuring anybody: they
 * need to know whether it is them or us.
 *
 * One per page, at the top of the content area, above whatever skeleton is
 * waiting — drawn by the shell rather than by each skeleton, so a page with
 * three waiting panels says it once. Retry asks again: each waiting page's own
 * reload where it gave one, else the page mounted afresh.
 */
const props = defineProps({
  after: { type: Number, default: 5000 },
});

const slow = ref(false);
let timer = null;

const arm = () => {
  clearTimeout(timer);
  slow.value = false;
  timer = setTimeout(() => { slow.value = true; }, props.after);
};

const busy = computed(isBusy);
watch(busy, (now) => {
  if (now) arm();
  else {
    clearTimeout(timer);
    slow.value = false;
  }
}, { immediate: true });

const again = () => {
  retry();
  // The clock starts over: the notice comes back only if this wait is slow too.
  if (busy.value) arm();
};

onBeforeUnmount(() => clearTimeout(timer));
</script>

<template>
  <div v-if="slow" class="xl-slow" role="status" data-testid="page-loading-slow">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    </svg>
    <p><b>Still loading</b> — your connection looks slow.</p>
    <XlButton variant="ghost" size="sm" data-testid="page-loading-retry" @click="again">Retry</XlButton>
  </div>
</template>

<style scoped>
.xl-slow {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 18px;
  padding: 13px 16px;
  border: 1px solid #EDE7F0;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(28, 23, 32, 0.04);
  font-family: var(--xl-font-family, inherit);
  font-size: 13.5px;
  color: #867D8D;
}

.xl-slow svg { flex: 0 0 auto; }
.xl-slow p { flex: 1; margin: 0; }
.xl-slow b { color: #1C1720; font-weight: 600; }
</style>
